import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { FileText, Download, HeartHandshake, Loader2 } from "lucide-react";

const ImpactReport = () => {
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/impact-report").then((r) => setRep(r.data)).finally(() => setLoading(false));
  }, []);

  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/impact-report/pdf");
      const bytes = atob(r.data.pdf_base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([arr], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = r.data.filename; a.click();
      URL.revokeObjectURL(url);
    } finally { setDownloading(false); }
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-4xl mb-1 flex items-center gap-3">Annual Impact Report <HeartHandshake className="w-7 h-7 text-primary" /></h1>
          <p className="text-muted-foreground">Your charitable giving through GIFTED — tax-ready.</p>
        </div>
        <button data-testid="download-report-btn" onClick={download} disabled={downloading || !rep?.count}
          className="flex items-center gap-2 bg-gradient-to-r from-primary to-secondary text-white rounded-2xl px-5 py-2.5 hover:opacity-90 disabled:opacity-50">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download PDF
        </button>
      </div>

      {loading ? (
        <div className="text-center py-24 text-muted-foreground">Compiling your impact…</div>
      ) : !rep?.count ? (
        <div className="surface rounded-[2rem] p-16 text-center">
          <FileText className="w-12 h-12 mx-auto mb-4 text-primary/60" />
          <h3 className="font-serif text-2xl mb-2">No donations yet</h3>
          <p className="text-muted-foreground">Choose &quot;Charitable Donation&quot; on a card, or allocate a Social Fund to charity.</p>
        </div>
      ) : (
        <div className="surface rounded-[1.75rem] p-8" data-testid="impact-report">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="text-sm text-muted-foreground">Tax Year {rep.year}</div>
              <div className="font-serif text-2xl">Charitable Giving Summary</div>
            </div>
            <div className="text-right">
              <div className="text-sm text-muted-foreground">Total</div>
              <div className="font-serif text-4xl gold-text" data-testid="impact-total">${rep.total.toFixed(2)}</div>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground border-b border-border">
                <th className="text-left py-2">Charity</th><th className="text-left py-2">EIN</th>
                <th className="text-left py-2">Date</th><th className="text-right py-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rep.donations.map((d, i) => (
                <tr key={i} className="border-b border-border/50">
                  <td className="py-2.5">{d.charity_name}</td>
                  <td className="py-2.5 text-muted-foreground">{d.ein}</td>
                  <td className="py-2.5 text-muted-foreground">{String(d.date).slice(0, 10)}</td>
                  <td className="py-2.5 text-right">${Number(d.amount).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-muted-foreground/70 mt-6">Each recipient is a registered 501(c)(3). Retain individual receipts for filing.</p>
        </div>
      )}
    </AppShell>
  );
};

export default ImpactReport;
